// PlatformTenants — 真實資料版（Route 15, PR #106）。
//
// 列出所有 tenant（GET /v1/tenants — super_admin 看全部）+「進入」按鈕呼
// /v1/platform/tenants/:id/impersonate 重簽 nexus_session 帶 acting_tenant_id。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, ArrowRight, Search, RefreshCw, ServerCrash, Inbox, Layers, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import EmptyState from "../../components/EmptyState.js";
import Skeleton from "../../components/Skeleton.js";
import { listTenants, friendlyTenantsError, type TenantRow } from "../../lib/tenantsApi.js";
import { impersonateTenant, friendlyPlatformError } from "../../lib/platformApi.js";
import TenantAiQuotaModal from "./TenantAiQuotaModal.js";

const STATUS_BADGE: Record<string, string> = { active: "badge-success", trial: "badge-warning", suspended: "badge-danger" };
const STATUS_LABEL: Record<string, string> = { active: "啟用", trial: "試用", suspended: "停用" };

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: TenantRow[] }
  | { status: "error"; message: string };

export default function PlatformTenants(): React.ReactElement {
  const nav = useNavigate();
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [quotaTenant, setQuotaTenant] = useState<TenantRow | null>(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try { const r = await listTenants(); setState({ status: "ready", rows: r.items }); }
    catch (e) { setState({ status: "error", message: friendlyTenantsError(e) }); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    if (state.status !== "ready") return [];
    if (!q) return state.rows;
    const n = q.toLowerCase();
    return state.rows.filter((t) => t.name.toLowerCase().includes(n) || t.slug.toLowerCase().includes(n));
  }, [state, q]);

  const enter = async (t: TenantRow) => {
    setBusy(t.id); setErr(null);
    try {
      await impersonateTenant(t.id);
      // Reload localStorage session via /me on /dashboard mount
      window.localStorage.removeItem("location.dashboard.session");
      nav(`/dashboard?actingTenant=${encodeURIComponent(t.id)}`);
      window.location.assign(`/dashboard`);
    } catch (e) { setErr(friendlyPlatformError(e)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border bg-bg-surface p-4">
        <h2 className="text-sm font-semibold flex items-center gap-2 text-text-primary">
          <Layers className="h-4 w-4 text-accent" /> 全部客戶單位
        </h2>
        <p className="text-2xs text-text-muted mt-1">
          所有已登記的客戶單位。點「進入此單位」即可代為操作該客戶的內容（會留下紀錄）。
        </p>
      </div>

      <div className="flex items-center gap-2">
        <label className="relative block flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-text-muted" />
          <input className="input pl-9" placeholder="搜尋客戶單位名稱 / 代號" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
          <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
        </button>
      </div>

      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}

      {state.status === "loading" && (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => <div key={i} className="card p-4"><Skeleton className="h-12 w-full" /></div>)}
        </div>
      )}
      {state.status === "error" && <EmptyState icon={ServerCrash} title="租戶列表載入失敗" description={state.message}
        action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />}
      {state.status === "ready" && filtered.length === 0 && (
        <EmptyState icon={Inbox} title="沒有符合條件的客戶單位" description={q ? "調整搜尋關鍵字試試。" : "目前還沒有任何客戶單位。"} />
      )}
      {state.status === "ready" && filtered.length > 0 && (
        <ul className="space-y-2">
          {filtered.map((t) => (
            <li key={t.id} className="card p-4 space-y-3">
              {/* Top: avatar + tenant identity + status badge */}
              <div className="flex items-start gap-3 min-w-0">
                <span
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded text-sm font-semibold uppercase text-text-inverse"
                  style={{ background: t.brand_primary ?? "#00d4ff" }}
                >
                  {t.slug.charAt(0)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-text-primary truncate">{t.name}</div>
                  <div className="text-2xs font-mono text-text-muted truncate">{t.slug}</div>
                </div>
                <span className={`${STATUS_BADGE[t.status] ?? "badge-default"} flex-shrink-0`}>
                  {STATUS_LABEL[t.status] ?? t.status}
                </span>
              </div>

              {/* Meta — plan badge + stats */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-text-muted">
                <span className="badge-default">{t.plan_label}</span>
                <span>地點 {t.location_count}</span>
                <span>建立 {t.created_at.slice(0, 10)}</span>
              </div>

              {/* Actions — full-width side-by-side on mobile so they're
                  easy to tap; right-aligned at natural width on sm+. */}
              <div className="flex items-center gap-2 pt-2 border-t border-border/40 sm:justify-end">
                <button
                  className="btn-ghost text-xs flex-1 justify-center sm:flex-initial"
                  onClick={() => setQuotaTenant(t)}
                  title="管理 AI 額度與加值"
                >
                  <Sparkles className="h-3.5 w-3.5" /> AI 額度
                </button>
                <button
                  className="btn-primary text-sm flex-1 justify-center sm:flex-initial"
                  onClick={() => void enter(t)}
                  disabled={busy === t.id || t.status === "suspended"}
                >
                  {busy === t.id ? "切換中…" : <>進入此單位 <ArrowRight className="h-4 w-4" /></>}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <TenantAiQuotaModal
        open={quotaTenant !== null}
        tenantId={quotaTenant?.id ?? null}
        tenantName={quotaTenant?.name ?? ""}
        onClose={() => setQuotaTenant(null)}
      />
    </div>
  );
}
